import React, { memo } from "react";
import Spacer from "../../Spacer";
import { useTranslation } from "react-i18next";
import { formatValidatedMeasurement } from "../../../utils/results/validatedRanges";
import { SHOW_FULL_RESULTS } from "../../../config/results";

function formatQuality(value, t) {
  if (!value) return "--";
  return value === "pass" ? t("common.pass", "Pass") : t("common.fail", "Fail");
}

export default memo(function SpirometryResultRow({
  time,
  date,
  fev1,
  fvc,
  fev1FvcRatio,
  pef,
  rr,
  signalQuality,
  spirometryQuality,
  onClick,
}) {
  const { t } = useTranslation();
  const signalQualityIsPass = signalQuality === "pass";
  const spirometryQualityIsPass =
    spirometryQuality == null || spirometryQuality === "pass";
  const allQualityIsPass = signalQualityIsPass && spirometryQualityIsPass;
  const hasLowSignalQuality =
    signalQuality === "fail" || spirometryQuality === "fail";
  const metricTextClass = allQualityIsPass
    ? "!text-primary"
    : "!text-greyscale-3";

  const renderMetric = (label, value, unit = null) => (
    <div className="flex flex-col items-center justify-start min-w-0 self-stretch gap-0">
      <div className="w-full flex items-start justify-center text-center leading-tight">
        <p>
          {label} <span className="muted-text text-xs">{unit}</span>
        </p>
      </div>
      <div
        className={`title-text text-center whitespace-nowrap ${metricTextClass}`}
      >
        {value}
      </div>
    </div>
  );

  return (
    <div className="w-full flex flex-col">
      <button
        type="button"
        onClick={onClick}
        className={[
          "w-full text-left py-2 flex-col items-center gap-4 bg-transparent",
          onClick ? "cursor-pointer" : "cursor-default",
        ].join(" ")}
        disabled={!onClick}
      >
        {/* Top: timestamp */}
        <div>
          <p className="muted-text">
            {time} {date}
          </p>
        </div>

        {/* Bottom: metrics */}
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          {SHOW_FULL_RESULTS ? (
            <div className="grid grid-cols-5 gap-2 items-stretch">
              {renderMetric(
                t("results.spiro.fev1", "FEV1"),
                formatValidatedMeasurement("fev1", fev1),
                t("results.spiro.FEV1Unit", "L"),
              )}
              {renderMetric(
                t("results.spiro.fvc", "FVC"),
                formatValidatedMeasurement("fvc", fvc),
                t("results.spiro.FVCUnit", "L"),
              )}
              {renderMetric(
                t("results.spiro.fev1FvcRatio", "FEV1/FVC"),
                formatValidatedMeasurement("fev1FvcRatio", fev1FvcRatio),
              )}
              {renderMetric(
                t("results.spiro.pef", "PEF"),
                formatValidatedMeasurement("pef", pef),
                t("results.spiro.PEFUnit", "L/min"),
              )}
              {renderMetric(
                t("result.resp.rrShortLabel", "RR"),
                formatValidatedMeasurement("rr", rr),
                t("result.resp.rrUnit", "bpm"),
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1 items-stretch ">
              <div className="flex flex-col text-center items-center justify-between min-w-0 self-stretch">
                <div className="w-full flex items-start justify-center text-center leading-tight">
                  <p> {t("result.spiro.signalQuality", "Signal Quality")}</p>
                </div>
                <div
                  className={`title-text whitespace-nowrap ${allQualityIsPass ? "!text-primary" : "!text-greyscale-3"}`}
                >
                  {formatQuality(signalQuality, t)}
                </div>
              </div>

              <div className="flex flex-col text-center items-center justify-between min-w-0 self-stretch">
                <div className="w-full flex items-start justify-center text-center leading-tight">
                  <p>
                    {t("result.spiro.spirometryQuality", "Spirometry Quality")}
                  </p>
                </div>

                <div
                  className={`title-text whitespace-nowrap ${allQualityIsPass ? "!text-primary" : "!text-greyscale-3"}`}
                >
                  {formatQuality(spirometryQuality, t)}
                </div>
              </div>
            </div>
          )}

          {hasLowSignalQuality ? (
            <div className="muted-text text-center text-xs leading-tight">
              {t("result.spiro.qualityFailTitle", "Low signal quality.")}{" "}
              {t("result.spiro.qualityFailSubtitle", "Please try again.")}
            </div>
          ) : null}
        </div>
      </button>

      <Spacer />
    </div>
  );
});
